// Tenantra Inc. - Professional Landlord Dashboard (Rogers-inspired)
import { useEffect, useState } from 'react';
import { 
  Building2, Home, Users, FileText, AlertTriangle, 
  CheckCircle, Clock, Plus, ChevronRight, ChevronDown, LogOut, PanelLeftClose, PanelLeftOpen,
  LayoutDashboard, Shield, Menu, X, HelpCircle,
  Circle, ExternalLink, Phone, Mail, Send, Loader2,
  BookOpen, BadgeCheck, MessageCircle, CreditCard, User, Wallet,
  BarChart3, FileSpreadsheet, Headphones, Lock, Search, UserSearch, CalendarDays, ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCurrentUser, logout } from '@/services/auth';
import { useLocationSearch } from '@/services/useUrlParam';
import api from '@/services/api';
import { toast } from 'sonner';

// Import sub-pages
import PropertiesPage from './PropertiesPage';
import AddPropertyPage from './AddPropertyPage';
import PropertyVerifyPage from './PropertyVerifyPage';
import PhoneVerificationPage from './PhoneVerificationPage';
import VerificationPage from './VerificationPage';
import TenantsPage from './TenantsPage';
import AddTenancyPage from './AddTenancyPage';
import TenantDetailPage from './TenantDetailPage';
import EndTenancyPage from './EndTenancyPage';
import ReportsPage from './ReportsPage';
import SelectTenantForReportPage from './SelectTenantForReportPage';
import LandlordViewReportPage from './LandlordViewReportPage';
import ScreeningPage from './ScreeningPage';
import SubmitReportPage from './SubmitReportPage';
import EditReportPage from './EditReportPage';
import DisputesPage from './DisputesPage';
import DisputeDetailPage from './DisputeDetailPage';
import BillingPage from './BillingPage';
import ProfilePage from './ProfilePage';
import AnalyticsPage from './AnalyticsPage';
import PaymentsPage from './PaymentsPage';
import CalendarPage from './CalendarPage';
import WalletSetupPage from './WalletSetupPage';
import TeamPage from './TeamPage';
import BulkOperationsPage from './BulkOperationsPage';
import APIKeysPage from './APIKeysPage';
import ServicesPage from './ServicesPage';
import SupportPage from './SupportPage';
import SecurityPage from '@/pages/landlord/SecurityPage';
import { LogoMark } from '@/components/Logo';
import { CommandPalette, CommandTrigger } from '@/components/dashboard/CommandPalette';
import { ActionRequired, AttentionBell, NotificationsPage } from '@/components/dashboard/ActionRequired';
import { PortfolioStats } from '@/components/dashboard/PortfolioStats';
import { TableSkeleton } from '@/components/dashboard/TableKit';

// ============ Slide-out Panel Component ============
function SlideOutPanel({ isOpen, onClose, title, icon: Icon, children }) {
  if (!isOpen) return null;
  
  return (
    <>
      <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[3px] z-50 animate-in fade-in duration-200" onClick={onClose} data-testid="slide-panel-overlay" />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white/85 backdrop-blur-xl border-l border-white/60 shadow-2xl z-50 overflow-hidden flex flex-col animate-in slide-in-from-right duration-300 ease-out" data-testid="slide-panel">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-teal-600">
          <div className="flex items-center gap-3">
            {Icon && <Icon className="w-5 h-5 text-white" />}
            <h2 className="text-lg font-bold text-white">{title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </>
  );
}

// ============ Contact Us Form Component ============
function ContactUsForm({ onClose }) {
  const user = getCurrentUser();
  const [formData, setFormData] = useState({
    name: '',
    email: user?.email || '',
    subject: '',
    message: '',
    category: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact/submit', formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
        <p className="text-slate-600 mb-6">Thank you for reaching out. Our support team will get back to you within 24 hours.</p>
        <Button onClick={onClose} className="bg-teal-600 hover:bg-teal-700 text-white">Close</Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-slate-600 text-sm mb-4">Requests are answered by email within one business day.</p>
      
      <div className="space-y-2">
        <Label className="text-slate-700 text-sm">Your Name</Label>
        <Input 
          value={formData.name} 
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
          className="h-11 bg-slate-50 border-slate-200"
        />
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-700 text-sm">Email Address</Label>
        <Input 
          type="email"
          value={formData.email} 
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          placeholder="your@email.com"
          required
          className="h-11 bg-slate-50 border-slate-200"
        />
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-700 text-sm">Category</Label>
        <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
          <SelectTrigger className="h-11 bg-slate-50 border-slate-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="general">General Inquiry</SelectItem>
            <SelectItem value="technical">Technical Support</SelectItem>
            <SelectItem value="verification">Verification Help</SelectItem>
            <SelectItem value="billing">Billing Question</SelectItem>
            <SelectItem value="report">Report an Issue</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-700 text-sm">Subject</Label>
        <Input 
          value={formData.subject} 
          onChange={(e) => setFormData({...formData, subject: e.target.value})}
          required
          className="h-11 bg-slate-50 border-slate-200"
        />
      </div>
      
      <div className="space-y-2">
        <Label className="text-slate-700 text-sm">Message</Label>
        <Textarea 
          value={formData.message} 
          onChange={(e) => setFormData({...formData, message: e.target.value})}
          required
          rows={5}
          className="bg-slate-50 border-slate-200 resize-none"
        />
      </div>
      
      <Button type="submit" disabled={loading} className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-semibold">
        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
        {loading ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
}

// ============ Help panel primitives ============
function HelpSection({ title, children }) {
  return (
    <section className="space-y-2">
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
      {children}
    </section>
  );
}

function HelpRows({ rows }) {
  return (
    <dl className="border border-slate-200 rounded-md divide-y divide-slate-100">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[9rem_1fr] gap-3 px-3.5 py-2.5">
          <dt className="text-sm text-slate-500">{k}</dt>
          <dd className="text-sm text-slate-800">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ============ Support Center Content ============
function SupportCenterContent({ onContact }) {
  const [query, setQuery] = useState('');
  const faqs = [
    ['Adding a property', 'Properties → Add Property. Enter the address and upload a property tax bill or title document. A property must be verified before any report can be filed against it.'],
    ['Adding a tenant', 'Tenants → Add Tenant. Select the property, enter the tenant\'s legal name, date of birth, email and phone, and the move-in date and rent. The tenant receives a consent request by email; the tenancy becomes active once they approve.'],
    ['Filing a report', 'Reports → Submit Report. Choose an active tenancy and record the reporting period, rent payment status (on time, late, partial, missed), lease status, and any property issues. Reports can be edited within 30 days of filing.'],
    ['Disputes', 'A tenant may dispute any entry from their Rent Report. You are notified under Disputes and have 14 days to respond with supporting documents. Unresolved disputes are decided by Tenantra review staff; the entry may be upheld, corrected or removed.'],
    ['Verification time', 'Phone verification is immediate. Identity verification is usually completed within minutes; cases flagged for manual review are resolved within 1–2 business days. Property documents are reviewed within 1–2 business days.'],
    ['Multiple properties', 'There is no limit on properties per account. Each property is verified separately. Your plan sets how many properties can be active at one time; see Billing.'],
  ];

  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const matches = terms.length
    ? faqs.filter(([q, a]) => terms.every(t => (q + ' ' + a).toLowerCase().includes(t)))
    : faqs;

  return (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search help"
          className="h-10 pl-9 bg-white border-slate-300 text-sm focus:border-teal-600 focus:ring-teal-600/20"
          data-testid="help-search-input"
        />
      </div>

      <HelpSection title={terms.length ? `${matches.length} result${matches.length === 1 ? '' : 's'}` : 'Common questions'}>
        {matches.length === 0 ? (
          <div className="border border-slate-200 rounded-md px-3.5 py-4 text-sm text-slate-600" data-testid="help-no-results">
            No answer found for “{query}”.{' '}
            <button type="button" onClick={onContact} className="text-teal-700 font-medium hover:text-teal-800" data-testid="help-contact-btn">
              Write to support
            </button>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {matches.map(([q, a], i) => (
              <details key={q} className="group" open={terms.length > 0 && i === 0} data-testid="help-faq-item">
                <summary className="flex items-center justify-between px-3.5 py-3 cursor-pointer list-none text-sm font-medium text-slate-900">
                  {q}
                  <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="px-3.5 pb-3.5 text-sm text-slate-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        )}
      </HelpSection>

      <HelpSection title="Contact">
        <HelpRows rows={[
          ['Email', 'support@tenantra.ca'],
          ['Phone', '1-800-TENANTRA, Monday to Friday, 9:00–17:00 ET'],
          ['Written request', 'Use the Help button on this page. Requests are answered within one business day.'],
        ]} />
      </HelpSection>
    </div>
  );
}

// ============ How Reporting Works Content ============
function HowReportingWorksContent() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-700 leading-relaxed">
        A report is a record of one reporting period for one tenancy, filed by the landlord of record. Reports from all landlords
        are combined into the tenant's Rent Report and Tenant Score. Tenants can see every entry filed about them.
      </p>

      <HelpSection title="Who can file">
        <p className="text-sm text-slate-600 leading-relaxed">
          Only accounts with a verified phone number and verified government ID, filing against a verified property, for a
          tenancy the tenant has consented to. Each report is time-stamped and attributed to your account.
        </p>
      </HelpSection>

      <HelpSection title="What a report contains">
        <HelpRows rows={[
          ['Period', 'Start and end date covered by the report'],
          ['Rent payment', 'On time, late (with days late), partial, or missed'],
          ['Lease', 'In progress, fulfilled, or broken'],
          ['Issues', 'Damage, noise, unauthorized occupant, or other — each rated minor, moderate or severe'],
          ['Notes', 'Optional factual notes. Avoid opinion; the tenant can read them.'],
        ]} />
      </HelpSection>

      <HelpSection title="How the score is calculated">
        <HelpRows rows={[
          ['Payment history', '40 points'],
          ['Lease compliance', '30 points'],
          ['Property care', '30 points'],
          ['Bands', 'Excellent 85–100 · Good 70–84 · Fair 55–69 · Poor 0–54'],
          ['Minimum data', 'A score is shown once at least three reports exist'],
        ]} />
      </HelpSection>

      <HelpSection title="Corrections and disputes">
        <p className="text-sm text-slate-600 leading-relaxed">
          You can edit a report for 30 days after filing. After that, changes go through the dispute process. If a tenant
          disputes an entry, you have 14 days to respond; Tenantra review staff then uphold, correct or remove the entry.
          Removed entries no longer count toward the score.
        </p>
      </HelpSection>
    </div>
  );
}

// ============ Verification Guide Content ============
function VerificationGuideContent() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-700 leading-relaxed">
        Reports affect a person's ability to rent. Verification ties every report to a real, identifiable property owner or
        manager and to a property they actually control. Unverified accounts can browse but cannot file.
      </p>

      <HelpSection title="Account verification">
        <HelpRows rows={[
          ['1. Phone', 'A Canadian mobile number, confirmed by SMS code. Immediate.'],
          ['2. Identity', 'Government-issued photo ID (driver\'s licence, passport or PR card) and a live selfie, checked by our identity provider. Usually complete within minutes.'],
          ['Where', 'Security → Verification'],
        ]} />
      </HelpSection>

      <HelpSection title="Property verification">
        <HelpRows rows={[
          ['Owners', 'Current property tax bill or land title showing your name and the property address'],
          ['Managers', 'Signed property management agreement naming you or your company for that address'],
          ['Review', 'Documents are checked by Tenantra staff within 1–2 business days. You are notified by email.'],
          ['Where', 'Properties → select the property → Documents'],
        ]} />
      </HelpSection>

      <HelpSection title="Statuses">
        <HelpRows rows={[
          ['Pending', 'Submitted and waiting for review'],
          ['Verified', 'Approved; reports can be filed'],
          ['Needs documents', 'A document was unreadable or did not match; upload again'],
          ['Rejected', 'The document did not establish ownership or identity. Contact support if you believe this is an error.'],
        ]} />
      </HelpSection>

      <p className="text-xs text-slate-500 leading-relaxed">
        Identity documents are used only to confirm who you are and are handled under PIPEDA. They are not shown to tenants or other landlords.
      </p>
    </div>
  );
}

// Sidebar Component - Microsoft 365 Admin Style
// Maps a dashboard path to the gate-able section key (null = always available).
function getLandlordSection(path) {
  if (path.startsWith('/landlord/properties')) return 'properties';
  if (path.startsWith('/landlord/screening') || path === '/landlord/view-report') return 'screening';
  if (path.startsWith('/landlord/tenants')) return 'tenants';
  if (path.startsWith('/landlord/reports')) return 'reports';
  if (path.startsWith('/landlord/disputes')) return 'disputes';
  if (path.startsWith('/landlord/payments') || path.startsWith('/landlord/wallet')) return 'payments';
  if (path.startsWith('/landlord/calendar')) return 'calendar';
  if (path.startsWith('/landlord/analytics')) return 'analytics';
  if (path.startsWith('/landlord/bulk')) return 'bulk';
  if (path.startsWith('/landlord/api-keys')) return 'api_keys';
  if (path.startsWith('/landlord/support')) return 'support';
  return null;
}

// True when a team member may open this section.
function canAccessSection(teamAccess, section) {
  if (!teamAccess || !teamAccess.restricted || !section) return true;
  return (teamAccess.permissions || []).includes(section);
}


function Sidebar({ isOpen, onClose, counts, userData, billingPlan, teamAccess, collapsed, onToggle }) {
  const currentPath = window.location.pathname;
  const user = getCurrentUser();
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const isVerified = userData?.phone_verified && userData?.landlord?.id_verification_status === 'verified';
  const needsVerification = !!userData && !isVerified;
  
  // Main navigation items
  const navItems = [
    { href: '/landlord/dashboard', label: 'Home', icon: LayoutDashboard },
    { href: '/landlord/properties', label: 'Properties', icon: Home, count: counts?.properties, requiresPlan: true, children: [
      { href: '/landlord/properties', label: 'All properties' },
      { href: '/landlord/properties?status=verified', label: 'Verified' },
      { href: '/landlord/properties?status=pending', label: 'Pending verification' },
      { href: '/landlord/properties/new', label: 'Add property' },
    ] },
    { href: '/landlord/screening', label: 'Screening', icon: UserSearch, requiresPlan: true, children: [
      { href: '/landlord/screening', label: 'By address' },
      { href: '/landlord/view-report', label: 'Look up a rent report' },
    ]},
    { href: '/landlord/tenants', label: 'Tenants', icon: Users, count: counts?.tenants, requiresPlan: true, children: [
      { href: '/landlord/tenants', label: 'All tenants' },
      { href: '/landlord/tenants?status=active', label: 'Active' },
      { href: '/landlord/tenants?status=pending_consent', label: 'Consent pending' },
      { href: '/landlord/tenants?status=ended', label: 'Moved out' },
      { href: '/landlord/tenants/new', label: 'Add tenancy' },
    ] },
    { href: '/landlord/reports', label: 'Reports', icon: FileText, count: counts?.reports, requiresPlan: true, children: [
      { href: '/landlord/reports', label: 'All tenancies' },
      { href: '/landlord/reports/new', label: 'Submit report' },
    ] },
    { href: '/landlord/disputes', label: 'Disputes', icon: AlertTriangle, count: counts?.disputes, alert: counts?.disputes > 0, requiresPlan: true, children: [
      { href: '/landlord/disputes', label: 'All disputes' },
      { href: '/landlord/disputes?status=open', label: 'Awaiting response' },
      { href: '/landlord/disputes?status=under_review', label: 'Under review' },
      { href: '/landlord/disputes?status=resolved', label: 'Resolved' },
    ] },
    { href: '/landlord/payments', label: 'Payments', icon: Wallet, requiresPlan: true },
    { href: '/landlord/calendar', label: 'Calendar', icon: CalendarDays, requiresPlan: true },
    { href: '/landlord/analytics', label: 'Analytics', icon: BarChart3, requiresPlan: 'professional' },
    { href: '/landlord/team', label: 'Team', icon: Users, requiresPlan: 'professional' },
    { href: '/landlord/bulk', label: 'Bulk Operations', icon: FileSpreadsheet, requiresPlan: 'enterprise' },
    { href: '/landlord/api-keys', label: 'API Access', icon: Building2, requiresPlan: 'enterprise' },
    { href: '/landlord/support', label: 'Support', icon: Headphones, requiresPlan: 'professional' },
  ];
  
  // Filter navigation based on user's plan AND status
  const visibleNavItems = navItems.filter(item => {
    // Team members only see the sections the owner granted them.
    if (!canAccessSection(teamAccess, getLandlordSection(item.href))) return false;
    if (!item.requiresPlan) return true;
    if (!billingPlan) return item.requiresPlan === true;
    // If subscription is canceled/inactive, show only Starter features
    if (billingPlan.status !== 'active' || billingPlan.plan === 'none') {
      return item.requiresPlan === true;
    }
    // Active subscription - check tier
    if (item.requiresPlan === true) return true;
    if (item.requiresPlan === 'professional') return ['professional', 'enterprise'].includes(billingPlan.plan);
    if (item.requiresPlan === 'enterprise') return billingPlan.plan === 'enterprise';
    return true;
  });

  // Settings & Tools section
  const settingsItems = [
    { href: '/landlord/security', label: 'Security', icon: Shield, badge: needsVerification ? 'Verify' : null },
  ];

  const search = useLocationSearch();
  const isSubActive = (href) => {
    const url = new URL(href, window.location.origin);
    return url.pathname === currentPath && url.search === search;
  };
  const groupIsActive = (item) => isItemActive(item.href) || item.children?.some(c => isSubActive(c.href)) || (item.href === '/landlord/screening' && currentPath === '/landlord/view-report');
  const [openGroups, setOpenGroups] = useState(() => new Set(navItems.filter(i => i.children && groupIsActiveInit(i)).map(i => i.href)));
  function groupIsActiveInit(item) { return window.location.pathname.startsWith(item.href) || (item.href === '/landlord/screening' && window.location.pathname === '/landlord/view-report'); }
  const toggleGroup = (href) => setOpenGroups(prev => { const n = new Set(prev); if (n.has(href)) n.delete(href); else n.add(href); return n; });
  useEffect(() => {
    const active = navItems.find(i => i.children && groupIsActiveInit(i));
    if (active) setOpenGroups(new Set([active.href]));
  }, [currentPath]); // eslint-disable-line

  const isItemActive = (href) => {
    if (href === '/landlord/dashboard') {
      return currentPath === href || currentPath === '/landlord';
    }
    if (href === '/landlord/payments') return currentPath.startsWith(href) || currentPath.startsWith('/landlord/wallet');
    if (href === '/landlord/security') {
      return currentPath.startsWith(href) || currentPath.startsWith('/landlord/verify');
    }
    if (href === '/landlord/screening') return currentPath.startsWith(href) || currentPath === '/landlord/view-report';
    return currentPath.startsWith(href);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-[3px] z-40 lg:hidden animate-in fade-in duration-200" onClick={onClose} />
      )}

      <div className={`
        fixed top-0 bottom-0 left-0 bg-[#fafafa] flex flex-col z-50
        w-[280px] ${collapsed ? 'lg:w-[64px]' : 'lg:w-[240px]'}
        transition-transform duration-200 ease-out lg:transition-[width] lg:duration-200
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        shadow-[1px_0_0_rgba(15,23,42,0.04)]
      `}>
        {/* Header */}
        <div className={`h-14 flex items-center ${collapsed ? 'lg:justify-center px-4 lg:px-0' : 'justify-between px-4'}`}>
          <a href="/landlord/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <LogoMark className="w-7 h-7" />
            <span className={`font-bold tracking-tight text-slate-900 text-[15px] ${collapsed ? 'lg:hidden' : ''}`} style={{ fontFamily: "'Chivo', sans-serif" }}>Tenantra</span>
          </a>
          <button onClick={onClose} className="lg:hidden p-1.5 hover:bg-slate-200/60 rounded-md text-slate-500 transition-colors" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
          {!collapsed && (
            <button onClick={onToggle} className="hidden lg:inline-flex p-1.5 hover:bg-slate-200/60 rounded-md text-slate-400 hover:text-slate-700 transition-colors" aria-label="Collapse sidebar" title="Collapse sidebar" data-testid="sidebar-collapse-btn">
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
        </div>
        {collapsed && (
          <div className="hidden lg:flex justify-center pb-1">
            <button onClick={onToggle} className="p-1.5 hover:bg-slate-200/60 rounded-md text-slate-400 hover:text-slate-700 transition-colors" aria-label="Expand sidebar" title="Expand sidebar" data-testid="sidebar-expand-btn">
              <PanelLeftOpen className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto py-2 ${collapsed ? 'px-3 lg:px-2' : 'px-3'}`}>
          {/* Main Section Label */}
          <div className={`px-3 py-2 ${collapsed ? 'lg:hidden' : ''}`}>
            <span className="text-xs font-normal text-slate-400">Main</span>
          </div>
          
          {/* Main Nav Items */}
          <div className="space-y-0.5">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = isItemActive(item.href);
              const isLocked = !isVerified && item.href !== '/landlord/dashboard';
              
              const hasSubs = !!item.children && !isLocked;
              const isOpen = hasSubs && openGroups.has(item.href);
              const childActive = hasSubs && item.children.some(c => isSubActive(c.href));
              const slug = item.label.toLowerCase().replace(/\s+/g, '-');
              return (
                <div key={item.href} className="relative" data-testid={`nav-group-${slug}`}>
                <a
                  href={isLocked ? '/landlord/security' : item.href}
                  data-testid={`nav-${slug}`}
                  onClick={onClose}
                  title={collapsed ? item.label : undefined}
                  className={`
                    flex items-center gap-3 py-2.5 rounded-lg text-[13px] transition-all duration-200 relative group active:scale-[0.98]
                    ${collapsed ? 'px-3 lg:px-0 lg:justify-center' : 'px-3'} ${hasSubs ? (collapsed ? 'pr-9 lg:pr-0' : 'pr-9') : ''}
                    ${isLocked
                      ? 'text-slate-400 cursor-default'
                      : isActive 
                        ? `${childActive && !collapsed ? 'hover:bg-slate-100/70' : 'bg-teal-50'} text-teal-800 font-medium` 
                        : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 font-normal hover:translate-x-0.5'
                    }
                  `}
                >
                  {/* Active indicator - left border */}
                  {isActive && (
                    <span className="hidden" />
                  )}
                  <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${isLocked ? 'text-slate-300' : isActive ? 'text-teal-600' : item.alert ? 'text-red-500' : 'text-slate-400'}`} />
                  <span className={`flex-1 ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                  {isLocked ? (
                    <Lock className={`w-3.5 h-3.5 text-slate-300 ${collapsed ? 'lg:hidden' : ''}`} />
                  ) : item.count !== null && item.count !== undefined && item.count > 0 && (
                    <span className={`
                      text-[11px] font-medium px-1.5 py-0.5 rounded-full min-w-[20px] text-center transition-transform duration-200 group-hover:scale-105
                      ${item.alert ? 'bg-red-50 text-red-500' : 'bg-slate-100/80 text-slate-500'} ${collapsed ? 'lg:hidden' : ''}
                    `}>
                      {item.count}
                    </span>
                  )}
                  {collapsed && item.alert && <span className="hidden lg:block absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-red-500" />}
                </a>
                {hasSubs && (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleGroup(item.href)}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.label}`}
                      className={`absolute right-1.5 top-[7px] w-6 h-6 inline-flex items-center justify-center rounded-full transition-colors ${isOpen ? 'text-teal-600' : 'text-slate-300'} hover:text-slate-600 hover:bg-slate-200/50 ${collapsed ? 'lg:hidden' : ''}`}
                      data-testid={`nav-toggle-${slug}`}
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ease-out ${isOpen ? '' : '-rotate-90'}`} />
                    </button>
                    <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'} ${collapsed ? 'lg:hidden' : ''}`}>
                      <div className="overflow-hidden">
                        <div className="ml-[18px] pl-2 my-1 space-y-px" data-testid={`nav-subs-${slug}`}>
                          {item.children.map(c => {
                            const subActive = isSubActive(c.href);
                            return (
                              <a
                                key={c.href}
                                href={c.href}
                                onClick={onClose}
                                tabIndex={isOpen ? 0 : -1}
                                className={`flex items-center gap-2.5 pl-3 pr-2.5 py-[7px] rounded-lg text-[12.5px] transition-colors group/sub ${subActive ? 'bg-teal-50 text-teal-800 font-medium' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/70'}`}
                                data-testid={`nav-sub-${slug}-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <span className={`w-1 h-1 rounded-full flex-shrink-0 transition-colors ${subActive ? 'bg-teal-500' : 'bg-slate-300 group-hover/sub:bg-slate-400'}`} />
                                {c.label}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                </div>
              );
            })}
          </div>

          {/* Settings Section */}
          <div className="mt-6">
            <div className={`px-3 py-2 ${collapsed ? 'lg:hidden' : ''}`}>
              <span className="text-xs font-normal text-slate-400">Settings</span>
            </div>
            <div className="space-y-0.5">
              {settingsItems.map((item) => {
                const Icon = item.icon;
                const isActive = isItemActive(item.href);
                const isSecurityLocked = false;
                
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={onClose}
                    title={collapsed ? item.label : undefined}
                    className={`
                      flex items-center gap-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 relative ${collapsed ? 'px-3 lg:px-0 lg:justify-center' : 'px-3'}
                      ${isSecurityLocked
                        ? 'text-slate-400 cursor-default'
                        : isActive 
                          ? 'bg-teal-50 text-teal-800 font-medium' 
                          : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 font-normal'
                      }
                    `}
                  >
                    {isActive && !isSecurityLocked && (
                      <span className="hidden" />
                    )}
                    <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isSecurityLocked ? 'text-slate-300' : isActive ? 'text-teal-600' : 'text-slate-400'}`} />
                    <span className={`flex-1 ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                    {item.badge && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 ${collapsed ? 'lg:hidden' : ''}`} data-testid="security-verify-badge">{item.badge}</span>}
                    {collapsed && item.badge && <span className="hidden lg:block absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-amber-500" />}
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6">
            <div className={`px-3 py-2 ${collapsed ? 'lg:hidden' : ''}`}>
              <span className="text-xs font-normal text-slate-400">Account</span>
            </div>
            <div className="space-y-0.5">
              <a 
                href="/landlord/billing" 
                onClick={onClose}
                title={collapsed ? 'Billing' : undefined}
                className={`
                  flex items-center gap-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 relative ${collapsed ? 'px-3 lg:px-0 lg:justify-center' : 'px-3'}
                  ${isItemActive('/landlord/billing') 
                    ? 'bg-teal-50 text-teal-800 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 font-normal'
                  }
                `}
              >
                {isItemActive('/landlord/billing') && (
                  <span className="hidden" />
                )}
                <CreditCard className={`w-[18px] h-[18px] flex-shrink-0 ${isItemActive('/landlord/billing') ? 'text-teal-600' : 'text-slate-400'}`} />
                <span className={`flex-1 ${collapsed ? 'lg:hidden' : ''}`}>Billing</span>
              </a>
              <a 
                href="/landlord/profile" 
                onClick={onClose}
                title={collapsed ? 'Profile' : undefined}
                className={`
                  flex items-center gap-3 py-2.5 rounded-lg text-[13px] transition-all duration-150 relative ${collapsed ? 'px-3 lg:px-0 lg:justify-center' : 'px-3'}
                  ${isItemActive('/landlord/profile') 
                    ? 'bg-teal-50 text-teal-800 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 font-normal'
                  }
                `}
              >
                {isItemActive('/landlord/profile') && (
                  <span className="hidden" />
                )}
                <User className={`w-[18px] h-[18px] flex-shrink-0 ${isItemActive('/landlord/profile') ? 'text-teal-600' : 'text-slate-400'}`} />
                <span className={`flex-1 ${collapsed ? 'lg:hidden' : ''}`}>Profile</span>
              </a>
            </div>
          </div>
        </nav>
        
        {/* User Profile Footer - Modern Dropdown Style */}
        <div className={`border-t border-slate-200/80 bg-white/50 ${collapsed ? 'p-3 lg:p-2' : 'p-3'}`}>
          <div className="relative">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              title={collapsed ? user?.email : undefined}
              className={`w-full flex items-center gap-3 p-2 rounded-md hover:bg-slate-100/80 cursor-pointer transition-colors ${collapsed ? 'lg:justify-center lg:p-1.5' : ''}`}
              data-testid="sidebar-user-menu-btn"
            >
              <div className="w-8 h-8 rounded-full bg-teal-700 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                {(userData?.display_name || userData?.landlord?.legal_name || user?.email)?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className={`flex-1 min-w-0 text-left ${collapsed ? 'lg:hidden' : ''}`}>
                <div className="text-[13px] font-medium text-slate-800 truncate">{userData?.display_name || userData?.landlord?.legal_name || userData?.landlord?.business_name || user?.email?.split('@')[0] || 'User'}</div>
                <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
              </div>
              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showUserMenu ? 'rotate-90' : ''} ${collapsed ? 'lg:hidden' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            {showUserMenu && (
              <>
                {/* Click outside overlay */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowUserMenu(false)}
                />
                <div className={`absolute bottom-full left-0 mb-1 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-200 ${collapsed ? 'right-0 lg:right-auto lg:min-w-[160px]' : 'right-0'}`}>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-[13px] text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4 text-slate-400" />
                    <span>Sign out</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function LandlordDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('tenantra.sidebar.collapsed') === '1');
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    document.body.classList.add('ll-soft');
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { document.body.classList.remove('ll-soft'); window.removeEventListener('scroll', onScroll); };
  }, []);
  const toggleSidebar = () => setSidebarCollapsed((c) => { localStorage.setItem('tenantra.sidebar.collapsed', c ? '0' : '1'); return !c; });
  const currentPath = window.location.pathname;
  useEffect(() => { setSidebarOpen(false); if (dashboard) fetchDashboard(); }, [currentPath]);
  const user = getCurrentUser();
  const [billingPlan, setBillingPlan] = useState(null);
  const [teamAccess, setTeamAccess] = useState(null);
  
  // Slide-out panel states
  const [contactPanelOpen, setContactPanelOpen] = useState(false);
  const [supportPanelOpen, setSupportPanelOpen] = useState(false);
  const [reportingPanelOpen, setReportingPanelOpen] = useState(false);
  const [verificationPanelOpen, setVerificationPanelOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchUserData();
    fetchBillingPlan();
    fetchTeamAccess();
  }, []);

  const fetchTeamAccess = async () => {
    try {
      const response = await api.get('/teams/me/access');
      setTeamAccess(response.data);
    } catch (err) {
      setTeamAccess({ restricted: false });
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/landlord/dashboard');
      setDashboard(response.data);
    } catch (err) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const [userData, setUserData] = useState(null);
  
  const fetchUserData = async () => {
    try {
      const response = await api.get('/auth/me');
      setUserData(response.data);
    } catch (err) {
      console.error('Failed to fetch user data');
    }
  };
  
  const fetchBillingPlan = async () => {
    try {
      const response = await api.get('/billing/current-plan');
      setBillingPlan(response.data);
    } catch (err) {
      console.error('Failed to fetch billing plan');
    }
  };

  const counts = {
    properties: dashboard?.property_count || 0,
    tenants: dashboard?.tenant_count || 0,
    reports: dashboard?.report_count || 0,
    disputes: dashboard?.active_dispute_count || 0,
  };

  const getPageTitle = () => {
    if (currentPath === '/landlord/dashboard' || currentPath === '/landlord') return 'Overview';
    if (currentPath.includes('view-report')) return 'Screening';
    if (currentPath.includes('screening')) return 'Screening';
    if (currentPath.includes('analytics')) return 'Analytics';
    if (currentPath.includes('/landlord/payments')) return 'Payments';
    if (currentPath.includes('/landlord/calendar')) return 'Calendar';
    if (currentPath.includes('/landlord/wallet')) return 'Rent wallet';
    if (currentPath.includes('notifications')) return 'Notifications';
    if (currentPath.includes('properties')) return 'Properties';
    if (currentPath.includes('tenants')) return 'Tenants';
    if (currentPath.includes('reports')) return 'Reports';
    if (currentPath.includes('disputes')) return 'Disputes';
    if (currentPath.includes('verify')) return 'Security';
    if (currentPath.includes('team')) return 'Team';
    if (currentPath.includes('billing')) return 'Billing';
    if (currentPath.includes('profile')) return 'Profile';
    if (currentPath.includes('security')) return 'Security';
    if (currentPath.includes('support')) return 'Support';
    if (currentPath.includes('api-keys')) return 'API Access';
    if (currentPath.includes('/landlord/services')) return 'Services';
    return 'Dashboard';
  };

  const renderContent = () => {
    // Verification gate: only allow billing, profile, verification, and home if not fully verified
    const isVerified = userData?.phone_verified && userData?.landlord?.id_verification_status === 'verified';
    const allowedUnverified = [
      '/landlord/dashboard', '/landlord',
      '/landlord/billing', '/landlord/profile', '/landlord/security',
      '/landlord/verify-phone', '/landlord/verify'
    ];
    const isAllowed = allowedUnverified.some(p => currentPath === p);

    if (!userData && !isAllowed) {
      return <TableSkeleton rows={6} spans={[4, 2, 2, 2, 2]} />;
    }

    if (!isVerified && !isAllowed) {
      return (
        <div className="max-w-md mx-auto text-center py-16" data-testid="verification-required-block">
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-base font-semibold text-slate-800 mb-1">Verification Required</h2>
          <p className="text-sm text-slate-500 mb-5">
            Complete phone and identity verification to access this feature.
          </p>
          <a href="/landlord/security" data-testid="verification-required-cta">
            <Button className="h-9 px-5 text-sm bg-teal-600 hover:bg-teal-700 text-white border-0">
              Complete Verification
            </Button>
          </a>
        </div>
      );
    }

    // Team-member section gate: block sections the owner didn't grant (even via direct URL).
    const section = getLandlordSection(currentPath);
    if (teamAccess && teamAccess.restricted && section && !canAccessSection(teamAccess, section)) {
      return (
        <div className="max-w-md mx-auto text-center py-16" data-testid="section-access-blocked">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-slate-400" />
          </div>
          <h2 className="text-base font-semibold text-slate-800 mb-1">Access restricted</h2>
          <p className="text-sm text-slate-500 mb-5">
            You don't have access to this section. Ask your team owner to grant it if you need it.
          </p>
          <a href="/landlord/dashboard">
            <Button className="h-9 px-5 text-sm bg-teal-600 hover:bg-teal-700 text-white border-0">
              Back to Home
            </Button>
          </a>
        </div>
      );
    }

    if (currentPath === '/landlord/dashboard' || currentPath === '/landlord') {
      return renderDashboardContent();
    }
    if (currentPath === '/landlord/analytics') return <AnalyticsPage />;
    if (currentPath === '/landlord/payments') return <PaymentsPage />;
    if (currentPath === '/landlord/calendar') return <CalendarPage />;
    if (currentPath === '/landlord/wallet') return <WalletSetupPage />;
    if (currentPath === '/landlord/notifications') return <NotificationsPage />;
    if (currentPath === '/landlord/properties') return <PropertiesPage />;
    if (currentPath === '/landlord/properties/new') return <AddPropertyPage />;
    if (currentPath.match(/^\/landlord\/properties\/[^/]+\/verify$/)) return <PropertyVerifyPage />;
    if (currentPath.match(/^\/landlord\/properties\/[^/]+$/) && !currentPath.includes('new')) return <PropertyVerifyPage />;
    if (currentPath === '/landlord/verify-phone') return <PhoneVerificationPage />;
    if (currentPath === '/landlord/verify') return <VerificationPage />;
    if (currentPath === '/landlord/tenants') return <TenantsPage />;
    if (currentPath === '/landlord/tenants/new') return <AddTenancyPage />;
    if (currentPath.match(/^\/landlord\/tenants\/[^/]+\/end$/)) return <EndTenancyPage />;
    if (currentPath.match(/^\/landlord\/tenants\/[^/]+$/) && !currentPath.includes('new')) return <TenantDetailPage />;
    if (currentPath === '/landlord/reports') return <ReportsPage />;
    if (currentPath === '/landlord/reports/new') return <SelectTenantForReportPage />;
    if (currentPath === '/landlord/view-report') return <LandlordViewReportPage />;
    if (currentPath === '/landlord/screening') return <ScreeningPage />;
    if (currentPath.match(/^\/landlord\/reports\/[^/]+\/edit$/)) return <EditReportPage />;
    if (currentPath.match(/^\/landlord\/reports\/[^/]+\/new$/)) return <SubmitReportPage />;
    if (currentPath === '/landlord/disputes') return <DisputesPage />;
    if (currentPath.match(/^\/landlord\/disputes\/[^/]+$/)) return <DisputeDetailPage />;
    if (currentPath === '/landlord/security') return <SecurityPage />;
    if (currentPath === '/landlord/billing') return <BillingPage />;
    if (currentPath === '/landlord/profile') return <ProfilePage />;
    if (currentPath === '/landlord/team') return <TeamPage />;
    if (currentPath === '/landlord/bulk') return <BulkOperationsPage />;
    if (currentPath === '/landlord/api-keys') return <APIKeysPage />;
    if (currentPath === '/landlord/services') return <ServicesPage />;
    if (currentPath === '/landlord/support') return <SupportPage />;
    
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Page not found</p>
        <a href="/landlord/dashboard" className="text-teal-600 hover:underline text-sm mt-2 inline-block">
          Back to Overview
        </a>
      </div>
    );
  };

  const renderDashboardContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-3 border-slate-200 border-t-teal-600 rounded-full animate-spin" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-16">
          <p className="text-slate-500 mb-4">{error}</p>
          <Button onClick={fetchDashboard} variant="outline">Retry</Button>
        </div>
      );
    }

    const isVerified = dashboard?.verification_status === 'verified';
    const userName = userData?.display_name || userData?.landlord?.legal_name || userData?.landlord?.business_name || user?.email?.split('@')[0] || 'Landlord';

    return (
      <div>
        {/* Welcome Header - Like Rogers */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">
            Welcome {userName.charAt(0).toUpperCase() + userName.slice(1)}
          </h1>
          <p className="text-slate-600">Account ID: {userData?.account_number || '------'}</p>
        </div>

        {isVerified && <ActionRequired />}

        {/* Main Content Grid - Like Rogers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* My Portfolio Card - Like "My bills" */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">My Portfolio</h2>
                  <button 
                    onClick={() => setContactPanelOpen(true)}
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1 hover:bg-teal-50 px-2 py-1 rounded-lg transition-colors"
                  >
                    <HelpCircle className="w-4 h-4" /> Help
                  </button>
                </div>

                <PortfolioStats reportCount={counts.reports} />

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <a href="/landlord/properties">
                    <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 h-9 text-sm w-full sm:w-auto">
                      View Properties
                    </Button>
                  </a>
                  <a href="/landlord/reports/new" className="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center gap-1 justify-center sm:justify-start py-1.5">
                    Submit a new report <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Status Row - Like Auto Payment / eBill */}
              <div className="border-t border-slate-200 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
                {/* Verification Status */}
                <div className="lens lens-card p-4 sm:p-5 bg-white">
                  <div className="flex items-center gap-2 mb-1.5">
                    <h3 className="text-sm font-bold text-slate-900">Verification</h3>
                    <span className={`flex items-center gap-1.5 text-sm font-medium ${isVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${isVerified ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                      {isVerified ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  {!isVerified && <p className="text-slate-600 text-sm mb-3">Complete verification to submit tenant reports.</p>}
                  {!isVerified && (
                    <a href="/landlord/security" className="text-teal-600 hover:text-teal-700 font-semibold text-sm">
                      Complete Verification
                    </a>
                  )}
                </div>

                {/* Subscription Status */}
                <div className="lens lens-card p-4 sm:p-5 bg-white">
                  <div className="flex items-center gap-2 mb-1.5">
                    <h3 className="text-sm font-bold text-slate-900">Subscription</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      billingPlan?.plan === 'enterprise' ? 'bg-teal-100 text-teal-700' :
                      billingPlan?.plan === 'professional' ? 'bg-purple-100 text-purple-700' :
                      billingPlan?.plan === 'starter' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {billingPlan?.plan === 'none' || !billingPlan ? 'No Plan' : 
                       billingPlan.plan.charAt(0).toUpperCase() + billingPlan.plan.slice(1)}
                    </span>
                  </div>
                  {billingPlan && billingPlan.plan !== 'none' ? (
                    <>
                      {(
                        <>
                          <p className="text-slate-600 text-sm mb-1">
                            Properties: {billingPlan.properties_used} / {billingPlan.property_limit || '∞'}
                          </p>
                          {billingPlan.property_limit && (
                            <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
                              <div
                                className={`h-1.5 rounded-full ${
                                  (billingPlan.properties_used / billingPlan.property_limit) >= 1 ? 'bg-red-500' :
                                  (billingPlan.properties_used / billingPlan.property_limit) >= 0.8 ? 'bg-amber-500' :
                                  'bg-teal-600'
                                }`}
                                style={{ width: `${Math.min(100, (billingPlan.properties_used / billingPlan.property_limit) * 100)}%` }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-600 text-sm mb-3">
                      Subscribe to start adding properties and managing tenants.
                    </p>
                  )}
                  <a href="/landlord/billing" className="text-teal-600 hover:text-teal-700 font-semibold text-sm">
                    {billingPlan?.plan === 'none' || !billingPlan ? 'Subscribe Now' : 'Manage Plan'}
                  </a>
                </div>

                {/* Reports Status */}
                <div className="lens lens-card p-5 sm:p-6 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-sm font-bold text-slate-900">Tenant Reports</h3>
                    <span className={`flex items-center gap-1.5 text-sm font-medium ${counts.reports > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${counts.reports > 0 ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                      {counts.reports > 0 ? 'Active' : 'None'}
                    </span>
                  </div>
                  {counts.reports === 0 && <p className="text-slate-600 text-sm mb-3">No tenant reports submitted yet.</p>}
                  <a href="/landlord/reports" className="text-teal-600 hover:text-teal-700 font-semibold text-sm">
                    View Reports
                  </a>
                </div>
              </div>
            </div>

            {/* Properties List */}
            <div className="lens lens-card bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-slate-900">Recent Properties</h2>
                <a href="/landlord/properties" className="text-teal-600 hover:text-teal-700 font-semibold text-sm">
                  View all
                </a>
              </div>
              
              {dashboard?.recent_properties?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {dashboard.recent_properties.slice(0, 4).map((property, i) => (
                    <a key={property.id || i} href={`/landlord/properties/${property.id}`} 
                       className="lens flex items-center gap-3 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-lg group">
                      <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Home className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{property.street_address || property.name}</p>
                        <p className="text-xs text-slate-400">{property.city}, {property.province}</p>
                      </div>
                      <div className="text-right hidden sm:block">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                          property.verification_status === 'verified' ? 'text-emerald-600' : 'text-amber-600'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            property.verification_status === 'verified' ? 'bg-emerald-500' : 'bg-amber-500'
                          }`}></span>
                          {property.verification_status === 'verified' ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Home className="w-5 h-5 text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-sm mb-3">No properties added yet</p>
                  <a href="/landlord/properties/new">
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm h-9">
                      <Plus className="w-4 h-4 mr-1.5" /> Add Your First Property
                    </Button>
                  </a>
                </div>
              )}
            </div>

            {/* Disputes Alert */}
            {counts.disputes > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">Disputes Requiring Attention</h2>
                    <p className="text-slate-500 text-xs">You have {counts.disputes} open dispute{counts.disputes > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <a href="/landlord/disputes">
                  <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 font-medium text-sm h-8">
                    View Disputes
                  </Button>
                </a>
              </div>
            )}
          </div>

          {/* Right Column - Help & Support */}
          <div className="space-y-6">
            {/* Help & Updates - Like Rogers */}
            <div className="lens lens-card bg-white rounded-xl shadow-sm border border-slate-200 p-5 sm:p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Help & Support</h2>
              <p className="text-slate-600 text-sm mb-6">
                Get help with your account, learn about our services, and find answers to common questions.
              </p>

              <div className="space-y-3">
                <button 
                  onClick={() => setSupportPanelOpen(true)}
                  className="lens w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 text-left active:scale-[0.98] group"
                >
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                    <BookOpen className="w-5 h-5 text-teal-600" />
                  </div>
                  <span className="font-semibold text-teal-600 flex-1">Support Center</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 transition-transform duration-200 group-hover:translate-x-1" />
                </button>

                <button 
                  onClick={() => setReportingPanelOpen(true)}
                  className="lens w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 text-left active:scale-[0.98] group"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="font-semibold text-teal-600 flex-1">How Reporting Works</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 transition-transform duration-200 group-hover:translate-x-1" />
                </button>

                <button 
                  onClick={() => setVerificationPanelOpen(true)}
                  className="lens w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 text-left active:scale-[0.98] group"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                    <BadgeCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-semibold text-teal-600 flex-1">Verification Guide</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="lens lens-card bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5">
              <h2 className="text-sm font-bold text-slate-900 mb-3">Quick Actions</h2>
              <div className="space-y-2">
                {/* Priority Support - Only for Professional & Enterprise */}
                {billingPlan?.plan && ['professional', 'enterprise'].includes(billingPlan.plan) && billingPlan.status === 'active' && (
                  <a href="/landlord/support" className="block group">
                    <Button variant="outline" className="w-full justify-start font-medium h-9 text-sm transition-colors group-hover:border-teal-300 group-hover:bg-teal-50/50">
                      <Headphones className="w-4 h-4 mr-2" /> Priority Support
                    </Button>
                  </a>
                )}
                <a href="/landlord/tenants/new" className="block group">
                  <Button variant="outline" className="w-full justify-start font-medium h-9 text-sm transition-colors group-hover:border-teal-300 group-hover:bg-teal-50/50">
                    <Users className="w-4 h-4 mr-2" /> Add Tenant
                  </Button>
                </a>
                <a href="/landlord/services" className="block group">
                  <Button variant="outline" className="w-full justify-start font-medium h-9 text-sm transition-colors group-hover:border-teal-300 group-hover:bg-teal-50/50">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Services
                  </Button>
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-slate-50 rounded-xl p-4 sm:p-5 border border-slate-200">
              <h3 className="font-bold text-slate-900 text-sm mb-2">Need Help?</h3>
              <div className="space-y-2 text-sm">
                <a href="mailto:support@tenantra.ca" className="flex items-center gap-2 text-slate-600 hover:text-teal-600">
                  <Mail className="w-4 h-4" /> support@tenantra.ca
                </a>
                <a href="tel:+18773520956" className="flex items-center gap-2 text-slate-600 hover:text-teal-600">
                  <Phone className="w-4 h-4" /> +1 877-352-0956
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        counts={counts} 
        userData={userData}
        billingPlan={billingPlan}
        teamAccess={teamAccess}
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />
      
      {/* Slide-out Panels */}
      <SlideOutPanel isOpen={contactPanelOpen} onClose={() => setContactPanelOpen(false)} title="Contact Us" icon={MessageCircle}>
        <ContactUsForm onClose={() => setContactPanelOpen(false)} />
      </SlideOutPanel>
      
      <SlideOutPanel isOpen={supportPanelOpen} onClose={() => setSupportPanelOpen(false)} title="Support Center" icon={BookOpen}>
        <SupportCenterContent onContact={() => { setSupportPanelOpen(false); setContactPanelOpen(true); }} />
      </SlideOutPanel>
      
      <SlideOutPanel isOpen={reportingPanelOpen} onClose={() => setReportingPanelOpen(false)} title="How Reporting Works" icon={FileText}>
        <HowReportingWorksContent />
      </SlideOutPanel>
      
      <SlideOutPanel isOpen={verificationPanelOpen} onClose={() => setVerificationPanelOpen(false)} title="Verification Guide" icon={BadgeCheck}>
        <VerificationGuideContent />
      </SlideOutPanel>
      
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} enabled={!!(userData?.phone_verified && userData?.landlord?.id_verification_status === 'verified')} />

      <main className={`min-h-screen transition-[margin] duration-200 ${sidebarCollapsed ? 'lg:ml-[64px]' : 'lg:ml-[240px]'}`}>
        {/* Top bar */}
        <div className={`sticky top-0 z-30 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/70 transition-shadow duration-200 ${scrolled ? 'shadow-[0_6px_18px_-12px_rgba(15,23,42,0.18)]' : ''}`} data-testid="dashboard-topbar">
          <div className="px-3 sm:px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 hover:bg-slate-100 rounded-lg text-slate-600" aria-label="Open menu" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-sm font-semibold text-slate-800">{getPageTitle()}</h1>
            </div>
            <div className="flex items-center gap-1.5">
              <CommandTrigger onClick={() => setPaletteOpen(true)} />
              <AttentionBell key={currentPath} enabled={dashboard?.verification_status === 'verified'} />
              <div className="hidden sm:flex items-center gap-2 ml-1.5 pl-1.5 border-l border-slate-200">
                <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-xs">
                  {(userData?.display_name || userData?.landlord?.legal_name || user?.email)?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="text-xs font-medium text-slate-700 max-w-[120px] truncate">{userData?.display_name || userData?.landlord?.legal_name || userData?.landlord?.business_name || user?.email?.split('@')[0]}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-5 lg:p-6 overflow-x-clip">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
